import { z } from "zod";
import { assertUnreachable } from "../helpers/utilities.js";
import { recurringScheduleSchema } from "../scheduling/recurringSchedule.js";
import {
  automationProviderChannelSchema,
  automationReportRoutingRuleSchema,
  checkRoutingUsesChannels,
  readInstanceReferenceId
} from "./automationReportRouting.js";
import { groupIdSchema } from "./group.js";
import { playbookIdSchema } from "./playbook.js";
import { teammateIdSchema } from "./teammate.js";
const automationIdSchema = z.uuid().brand();
function asAutomationId(id) {
  return automationIdSchema.parse(id);
}
function encodeAutomationResourceId(companyId, automationId) {
  return `company:${companyId}:automation:${automationId}`;
}
const AutomationTemplateIds = {
  TEAMMATE_COACHING: "teammate-coaching",
  CUSTOMER_PRAISE_DIGEST: "customer-praise-digest",
  /**
   * @deprecated Removed automation template. The ID "perfect-calls" and
   * name "Perfect calls" are reserved and must not be reused.
   */
  PERFECT_CALLS: "perfect-calls",
  STANDOUT_CALLS: "standout-calls",
  PLAYBOOK_SCORE_MOVEMENT: "playbook-score-movement",
  PLAYBOOK_FEEDBACK_ALERT: "playbook-feedback-alert",
  LEAD_BURNING: "lead-burning",
  CUSTOM: "custom"
};
const allAutomationTemplateIds = [
  AutomationTemplateIds.TEAMMATE_COACHING,
  AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST,
  AutomationTemplateIds.STANDOUT_CALLS,
  AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT,
  AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT,
  AutomationTemplateIds.LEAD_BURNING,
  AutomationTemplateIds.CUSTOM
];
const automationTemplateIdSchema = z.enum(allAutomationTemplateIds);
function checkIsOrchestratedAutomationTemplate(templateId) {
  return templateId === AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT || templateId === AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT || templateId === AutomationTemplateIds.LEAD_BURNING || templateId === AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST || templateId === AutomationTemplateIds.STANDOUT_CALLS || templateId === AutomationTemplateIds.CUSTOM;
}
const LEAD_BURNING_DAILY_TIME_OF_DAY = "20:00";
const allAutomationManagerScopes = [
  "all_managers",
  "specific_managers"
];
const automationManagerScopeSchema = z.enum(allAutomationManagerScopes);
const automationManagerRecipientsSchema = z.discriminatedUnion("scope", [
  z.object({ scope: z.literal("all_managers") }),
  z.object({
    scope: z.literal("specific_managers"),
    teammate_ids: z.array(teammateIdSchema)
  })
]);
const automationSubscriberRecipientsSchema = z.object({
  teammate_ids: z.array(teammateIdSchema)
});
const AutomationDeliveryProvider = {
  EMAIL: "email",
  SLACK: "slack",
  TEAMS: "teams"
};
const automationDeliveryProviderSchema = z.enum([
  AutomationDeliveryProvider.EMAIL,
  AutomationDeliveryProvider.SLACK,
  AutomationDeliveryProvider.TEAMS
]);
const automationChatDeliveryModeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("direct_message") }),
  z.object({
    kind: z.literal("channel"),
    channels: z.array(automationProviderChannelSchema)
  })
]);
const automationDeliverySchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal(AutomationDeliveryProvider.EMAIL) }),
  z.object({
    provider: z.literal(AutomationDeliveryProvider.SLACK),
    destination: automationChatDeliveryModeSchema
  }),
  z.object({
    provider: z.literal(AutomationDeliveryProvider.TEAMS),
    destination: automationChatDeliveryModeSchema
  })
]);
function buildDefaultAutomationDeliveryForProvider(provider) {
  switch (provider) {
    case AutomationDeliveryProvider.EMAIL:
      return { provider };
    case AutomationDeliveryProvider.SLACK:
    case AutomationDeliveryProvider.TEAMS:
      return { provider, destination: { kind: "direct_message" } };
    default:
      return assertUnreachable(provider);
  }
}
const DEFAULT_AUTOMATION_DELIVERY = {
  provider: AutomationDeliveryProvider.EMAIL
};
const automationPeopleSelectionSchema = z.object({
  includes_all_teammates: z.boolean(),
  includes_all_managers: z.boolean(),
  teammate_ids: z.array(teammateIdSchema),
  group_ids: z.array(groupIdSchema)
});
const teammateCoachingDeliveryProviderSchema = z.enum([
  AutomationDeliveryProvider.EMAIL,
  AutomationDeliveryProvider.SLACK,
  AutomationDeliveryProvider.TEAMS
]);
const teammateCoachingTransportSchema = z.object({
  provider: teammateCoachingDeliveryProviderSchema
});
const teammateCoachingAutomationSettingsSchema = z.object({
  playbook_id: playbookIdSchema,
  /** "Coaching focus" guidance fed into the coaching automation. */
  coaching_focus: z.string(),
  recipients: automationPeopleSelectionSchema,
  transport: teammateCoachingTransportSchema.optional()
});
function readCoachingDeliveryProvider(settings) {
  return settings.transport?.provider ?? AutomationDeliveryProvider.EMAIL;
}
function updateCoachingDeliveryProvider(settings, provider) {
  return { ...settings, transport: { provider } };
}
const automationPlaybooksScopeSchema = z.array(playbookIdSchema).min(1).refine((playbookIds) => new Set(playbookIds).size === playbookIds.length, {
  message: "Playbook ids must be unique"
});
const automationTeammateSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    teammate_ids: z.array(teammateIdSchema),
    group_ids: z.array(groupIdSchema)
  })
]);
const automationGroupSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    group_ids: z.array(groupIdSchema)
  })
]);
const automationReportInstancesSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("shared"),
    selection: automationTeammateSelectionSchema
  }),
  z.object({
    type: z.literal("teammate"),
    selection: automationTeammateSelectionSchema
  }),
  z.object({
    type: z.literal("manager"),
    selection: automationTeammateSelectionSchema
  }),
  z.object({
    type: z.literal("group"),
    selection: automationGroupSelectionSchema
  })
]);
const automationReportSourceSchema = z.enum(["calls", "rollup"]);
const allowedRoutingTypesByInstanceKind = {
  shared: ["recipients", "channels"],
  teammate: ["instance_recipient"],
  manager: ["recipients", "channels", "instance_recipient", "mapped"],
  group: ["recipients", "channels", "mapped"]
};
function refineReportDefinition(definition, ctx) {
  const { instances, source, routing } = definition;
  if (instances.type === "teammate" && source === "rollup") {
    ctx.addIssue({
      code: "custom",
      message: "A teammate's only report about them is their own calls report",
      path: ["source"]
    });
  }
  const allowedTypes = allowedRoutingTypesByInstanceKind[instances.type];
  if (!allowedTypes.includes(routing.type)) {
    ctx.addIssue({
      code: "custom",
      message: `A ${instances.type} report definition cannot route by ${routing.type}`,
      path: ["routing"]
    });
    return;
  }
  if (instances.type === "shared" && routing.type === "recipients" && (routing.subscribers.teammate_ids.length > 0 || routing.subscribers.group_ids.length > 0)) {
    ctx.addIssue({
      code: "custom",
      message: "Custom automation subscribers are not supported yet",
      path: ["routing", "subscribers"]
    });
  }
  if (routing.type !== "mapped") {
    return;
  }
  const seenIds = /* @__PURE__ */ new Set();
  routing.entries.forEach((entry, index) => {
    if (entry.instance.type !== instances.type) {
      ctx.addIssue({
        code: "custom",
        message: `Mapped routing of a ${instances.type} report definition takes ${instances.type} entries`,
        path: ["routing", "entries", index, "instance"]
      });
      return;
    }
    const id = readInstanceReferenceId(entry.instance);
    if (seenIds.has(id)) {
      ctx.addIssue({
        code: "custom",
        message: `Mapped routing may contain only one entry per ${instances.type}`,
        path: ["routing", "entries", index, "instance"]
      });
    }
    seenIds.add(id);
  });
}
const automationReportDefinitionSchema = z.object({
  instances: automationReportInstancesSchema,
  source: automationReportSourceSchema,
  routing: automationReportRoutingRuleSchema
}).superRefine(refineReportDefinition);
function buildReportDefinitionKey(definition) {
  return `${definition.instances.type}/${definition.source}`;
}
const automationReportConfigSchema = z.object({
  playbook_ids: automationPlaybooksScopeSchema,
  definitions: z.array(automationReportDefinitionSchema).min(1)
}).refine(
  ({ definitions }) => new Set(definitions.map(buildReportDefinitionKey)).size === definitions.length,
  {
    path: ["definitions"],
    message: "At most one report definition per instance kind and source"
  }
).refine(
  ({ definitions }) => !definitions.some((definition) => definition.source === "rollup") || definitions.some(
    (definition) => definition.source === "calls" && (definition.instances.type === "teammate" || definition.instances.type === "manager")
  ),
  {
    path: ["definitions"],
    message: "Rollups need a calls report definition about teammates or managers"
  }
);
const automationReportTransportSchema = z.object({
  provider: automationDeliveryProviderSchema
});
const formReportShapes = /* @__PURE__ */ new Set([
  "shared/calls",
  "teammate/calls",
  "manager/rollup,teammate/calls",
  "manager/calls"
]);
function checkIsFormReportShape(reportConfig) {
  const shape = reportConfig.definitions.map(buildReportDefinitionKey).sort().join(",");
  const selectsGroupsForTeamReports = reportConfig.definitions.some(
    ({ instances, source }) => instances.type === "manager" && source === "calls" && instances.selection.type === "specific" && instances.selection.group_ids.length > 0
  );
  return formReportShapes.has(shape) && !selectsGroupsForTeamReports;
}
const automationReportSettingsSchema = z.object({
  instructions: z.string(),
  // When the automation runs. Hourly runs only when there's new call activity;
  // the rest run at their configured local time. Which frequencies a
  // template's editor offers is form knowledge, not schema knowledge, so
  // every template persists the one shared union.
  trigger: recurringScheduleSchema,
  report_config: automationReportConfigSchema,
  transport: automationReportTransportSchema,
  /**
   * Set for the third result type, whose shape and delivery are described in
   * `instructions` rather than configured — the one result type that cannot be
   * read back off `report_config`, which is left as the editor last
   * configured it so switching back restores that configuration. Each run
   * derives its own definition and transport from the prompt instead.
   *
   * This is kept separately for flexibility. The settings schema has changed a
   * lot in v0 and a separate field is easier to migrate later.
   */
  is_custom_result_mode: z.boolean().optional()
}).refine((settings) => checkIsFormReportShape(settings.report_config), {
  path: ["report_config", "definitions"],
  message: "Reports must be one shared report, each person's own report with a manager rollup, or each manager's team report"
}).refine(
  (settings) => settings.transport.provider !== AutomationDeliveryProvider.EMAIL || !settings.report_config.definitions.some(
    (definition) => checkRoutingUsesChannels(definition.routing)
  ),
  {
    path: ["report_config", "definitions"],
    message: "Email delivery does not support channel destinations"
  }
);
const customAutomationSettingsSchema = automationReportSettingsSchema;
const playbookFeedbackAlertSettingsSchema = customAutomationSettingsSchema.refine(
  // Annotated so the refine validates without narrowing the settings type.
  (settings) => settings.trigger.frequency !== "hourly",
  {
    path: ["trigger"],
    message: "Playbook feedback alerts do not support hourly schedules"
  }
);
const managerReportSettingsSchema = automationReportSettingsSchema.refine(
  (settings) => settings.report_config.definitions.every(
    (definition) => definition.instances.type === "manager"
  ),
  {
    path: ["report_config", "definitions"],
    message: "Configured manager-report templates require manager instances"
  }
);
function buildEmptyRecipientSelection() {
  return { teammate_ids: [], group_ids: [] };
}
function findReportDefinition(reportConfig, kind) {
  return reportConfig.definitions.find(
    (definition) => definition.instances.type === kind
  );
}
function findCallsReportDefinition(reportConfig) {
  return reportConfig.definitions.find(
    (definition) => definition.source === "calls"
  );
}
function findPersonSelection(reportConfig, kind) {
  for (const { instances } of reportConfig.definitions) {
    if (instances.type === kind) {
      return instances.selection;
    }
  }
  return void 0;
}
function findInstanceRecipientRouting(reportConfig) {
  return reportConfig.definitions.map((definition) => definition.routing).find((routing) => routing.type === "instance_recipient") ?? {
    type: "instance_recipient",
    subscribers: buildEmptyRecipientSelection()
  };
}
function updateReportDefinitions(settings, update) {
  return {
    ...settings,
    report_config: {
      ...settings.report_config,
      definitions: settings.report_config.definitions.map(update)
    }
  };
}
function checkIsSharedReportConfig(reportConfig) {
  return reportConfig.definitions.length === 1 && reportConfig.definitions[0]?.instances.type === "shared";
}
function getAutomationDeliveryEditorValue(settings) {
  const { provider } = settings.transport;
  if (provider === AutomationDeliveryProvider.EMAIL) {
    return { provider };
  }
  const channelRouting = settings.report_config.definitions.map((definition) => definition.routing).find((routing) => routing.type === "channels");
  return {
    provider,
    destination: channelRouting ? { kind: "channel", channels: channelRouting.channels } : { kind: "direct_message" }
  };
}
function updateAutomationDeliveryEditorValue(settings, delivery) {
  return updateReportDefinitions(
    { ...settings, transport: { provider: delivery.provider } },
    (definition) => updateReportDefinitionRouting(definition, delivery)
  );
}
function updateReportDefinitionRouting(definition, delivery) {
  const channelDestination = delivery.provider !== AutomationDeliveryProvider.EMAIL && delivery.destination.kind === "channel" ? delivery.destination : null;
  const { routing } = definition;
  switch (definition.instances.type) {
    case "manager":
      if (channelDestination) {
        return {
          ...definition,
          routing: { type: "channels", channels: channelDestination.channels }
        };
      }
      return routing.type === "instance_recipient" ? definition : {
        ...definition,
        routing: {
          type: "instance_recipient",
          subscribers: buildEmptyRecipientSelection()
        }
      };
    case "shared":
      if (channelDestination) {
        return {
          ...definition,
          routing: { type: "channels", channels: channelDestination.channels }
        };
      }
      return routing.type === "recipients" ? definition : {
        ...definition,
        routing: {
          type: "recipients",
          primary: buildEmptyRecipientSelection(),
          subscribers: buildEmptyRecipientSelection()
        }
      };
    case "teammate":
    case "group":
      return definition;
    default:
      return assertUnreachable(definition.instances);
  }
}
function getAutomationManagerRecipientsEditorValue(reportConfig) {
  const selection = findPersonSelection(reportConfig, "manager");
  return selection?.type === "specific" ? { scope: "specific_managers", teammate_ids: selection.teammate_ids } : { scope: "all_managers" };
}
function updateAutomationManagerRecipientsEditorValue(settings, recipients) {
  return updateReportDefinitions(
    settings,
    (definition) => definition.instances.type === "manager" ? {
      ...definition,
      instances: {
        type: "manager",
        selection: recipients.scope === "all_managers" ? { type: "all" } : {
          type: "specific",
          teammate_ids: recipients.teammate_ids,
          group_ids: []
        }
      }
    } : definition
  );
}
function getAutomationSubscribersEditorValue(reportConfig) {
  return {
    teammate_ids: findInstanceRecipientRouting(reportConfig).subscribers.teammate_ids
  };
}
function updateAutomationSubscribersEditorValue(settings, subscribers) {
  return updateReportDefinitions(
    settings,
    (definition) => definition.routing.type === "instance_recipient" ? {
      ...definition,
      routing: {
        ...definition.routing,
        subscribers: {
          teammate_ids: subscribers.teammate_ids,
          group_ids: definition.routing.subscribers.group_ids
        }
      }
    } : definition
  );
}
const allAutomationResultModes = ["shared", "own", "custom"];
const automationResultModeSchema = z.enum(allAutomationResultModes);
function updateInstancesSelection(instances, selection) {
  switch (instances.type) {
    case "teammate":
      return {
        type: "teammate",
        selection: {
          type: "specific",
          teammate_ids: selection.teammate_ids,
          group_ids: selection.group_ids
        }
      };
    // The form selects a group's managers only through the rollup.
    case "manager":
      return {
        type: "manager",
        selection: {
          type: "specific",
          teammate_ids: selection.teammate_ids,
          group_ids: []
        }
      };
    case "group":
      return {
        type: "group",
        selection: { type: "specific", group_ids: selection.group_ids }
      };
    default:
      return assertUnreachable(instances);
  }
}
function getAutomationRecipientSelection(reportConfig) {
  const definition = findCallsReportDefinition(reportConfig);
  if (!definition) {
    return buildEmptyRecipientSelection();
  }
  const { instances, routing } = definition;
  switch (instances.type) {
    case "shared":
      return routing.type === "recipients" ? routing.primary : buildEmptyRecipientSelection();
    case "teammate":
    case "manager":
      return instances.selection.type === "specific" ? {
        teammate_ids: instances.selection.teammate_ids,
        group_ids: instances.selection.group_ids
      } : buildEmptyRecipientSelection();
    case "group":
      return instances.selection.type === "specific" ? { teammate_ids: [], group_ids: instances.selection.group_ids } : buildEmptyRecipientSelection();
    default:
      return assertUnreachable(instances);
  }
}
function buildAutomationReportConfig(mode, playbookIds, selection) {
  if (mode === "own") {
    return {
      playbook_ids: [...playbookIds],
      definitions: [
        {
          instances: { type: "teammate", selection: { type: "all" } },
          source: "calls",
          routing: {
            type: "instance_recipient",
            subscribers: buildEmptyRecipientSelection()
          }
        },
        {
          instances: { type: "manager", selection: { type: "all" } },
          source: "rollup",
          routing: {
            type: "instance_recipient",
            subscribers: buildEmptyRecipientSelection()
          }
        }
      ]
    };
  }
  return {
    playbook_ids: [...playbookIds],
    definitions: [
      {
        instances: { type: "shared", selection: { type: "all" } },
        source: "calls",
        routing: {
          type: "recipients",
          primary: selection,
          subscribers: buildEmptyRecipientSelection()
        }
      }
    ]
  };
}
function getAutomationResultMode(settings) {
  if (settings.is_custom_result_mode) {
    return "custom";
  }
  return checkIsSharedReportConfig(settings.report_config) ? "shared" : "own";
}
function updateAutomationResultMode(settings, mode) {
  if (mode === "custom") {
    return { ...settings, is_custom_result_mode: true };
  }
  const { is_custom_result_mode: _custom, ...configured } = settings;
  const reportConfig = configured.report_config;
  const isShared = checkIsSharedReportConfig(reportConfig);
  if (mode === "own" && !isShared) {
    return configured;
  }
  if (mode === "shared" && isShared) {
    return configured;
  }
  return {
    ...configured,
    report_config: buildAutomationReportConfig(
      mode,
      reportConfig.playbook_ids,
      getAutomationRecipientSelection(reportConfig)
    )
  };
}
function expandAutomationPeopleSelection(people, groups) {
  const groupIds = new Set(people.groupIds ?? []);
  return {
    ...people,
    groupIds: [],
    teammateIds: [
      .../* @__PURE__ */ new Set([
        ...people.teammateIds,
        ...groups.filter((group) => groupIds.has(group.id)).flatMap((group) => group.teammateIds)
      ])
    ]
  };
}
function describeAutomationPeopleSelection(people) {
  return [
    people.includesAllTeammates ? "All teammates" : null,
    people.includesAllManagers ? "All managers" : null,
    people.teammateIds.length ? `${people.teammateIds.length} selected ${people.teammateIds.length === 1 ? "person" : "people"}` : null,
    people.groupIds?.length ? `${people.groupIds.length} teammate ${people.groupIds.length === 1 ? "group" : "groups"}` : null
  ].filter(Boolean).join(", ") || "No one";
}
function checkCoachingHasRecipients(settings, role) {
  const people = settings.recipients;
  return (role === "teammate" ? people.includes_all_teammates : people.includes_all_managers) || people.teammate_ids.length > 0 || people.group_ids.length > 0;
}
function getCoachingRecipients(settings) {
  const people = settings.recipients;
  return {
    includesAllTeammates: people.includes_all_teammates,
    includesAllManagers: people.includes_all_managers,
    teammateIds: people.teammate_ids,
    groupIds: people.group_ids
  };
}
function updateCoachingRecipients(settings, people) {
  return {
    ...settings,
    recipients: {
      includes_all_teammates: people.includesAllTeammates,
      includes_all_managers: people.includesAllManagers,
      teammate_ids: [...people.teammateIds],
      group_ids: [...people.groupIds ?? []]
    }
  };
}
function partitionAutomationPeopleSelection(people, managerTeammateIds) {
  const managerIdSet = new Set(managerTeammateIds);
  const managerIds = people.includesAllManagers ? [] : people.teammateIds.filter((teammateId) => managerIdSet.has(teammateId));
  const teammateIds = people.includesAllTeammates ? [] : people.teammateIds.filter((teammateId) => !managerIdSet.has(teammateId));
  return {
    teammateIds,
    managerIds,
    hasTeammates: people.includesAllTeammates || teammateIds.length > 0,
    hasManagers: people.includesAllManagers || managerIds.length > 0
  };
}
function getAutomationPeopleSelection(reportConfig) {
  const teammates = findPersonSelection(reportConfig, "teammate");
  const managers = findPersonSelection(reportConfig, "manager");
  const picked = [teammates, managers].flatMap(
    (selection) => selection?.type === "specific" ? [selection] : []
  );
  const groupIds = [
    ...new Set(picked.flatMap((selection) => selection.group_ids))
  ];
  return {
    ...groupIds.length > 0 ? { groupIds } : {},
    includesAllTeammates: teammates?.type === "all",
    includesAllManagers: managers?.type === "all",
    teammateIds: [
      ...new Set(picked.flatMap((selection) => selection.teammate_ids))
    ]
  };
}
function updateAutomationPeopleSelection(settings, people, managerTeammateIds) {
  const reportConfig = settings.report_config;
  if (checkIsSharedReportConfig(reportConfig)) {
    return settings;
  }
  const { includesAllTeammates, includesAllManagers } = people;
  const groupIds = [...people.groupIds ?? []];
  const {
    teammateIds: pickedTeammateIds,
    managerIds: pickedManagerIds,
    hasTeammates
  } = partitionAutomationPeopleSelection(people, managerTeammateIds);
  const privateRouting = findInstanceRecipientRouting(reportConfig);
  const teammateRouting = findReportDefinition(reportConfig, "teammate")?.routing ?? privateRouting;
  const managerRouting = findReportDefinition(reportConfig, "manager")?.routing ?? privateRouting;
  const managerSelection = includesAllManagers ? { type: "all" } : pickedManagerIds.length > 0 || groupIds.length > 0 ? {
    type: "specific",
    teammate_ids: pickedManagerIds,
    group_ids: groupIds
  } : null;
  const definitions = managerSelection && !hasTeammates && groupIds.length === 0 ? [
    {
      instances: { type: "manager", selection: managerSelection },
      source: "calls",
      routing: managerRouting
    }
  ] : [
    {
      instances: {
        type: "teammate",
        selection: includesAllTeammates ? { type: "all" } : {
          type: "specific",
          teammate_ids: pickedTeammateIds,
          group_ids: groupIds
        }
      },
      source: "calls",
      routing: teammateRouting
    },
    ...managerSelection ? [
      {
        instances: {
          type: "manager",
          selection: managerSelection
        },
        source: "rollup",
        routing: managerRouting
      }
    ] : []
  ];
  return {
    ...settings,
    report_config: { ...reportConfig, definitions }
  };
}
function updateAutomationRecipientSelection(settings, selection) {
  const definition = findCallsReportDefinition(settings.report_config);
  if (!definition) {
    return settings;
  }
  const updated = definition.instances.type === "shared" ? {
    ...definition,
    routing: {
      type: "recipients",
      primary: selection,
      subscribers: definition.routing.type === "recipients" ? definition.routing.subscribers : buildEmptyRecipientSelection()
    }
  } : {
    ...definition,
    instances: updateInstancesSelection(definition.instances, selection)
  };
  return updateReportDefinitions(
    settings,
    (candidate) => candidate === definition ? updated : candidate
  );
}
function updateAutomationPlaybookIds(settings, playbookIds) {
  return {
    ...settings,
    report_config: {
      ...settings.report_config,
      playbook_ids: [...playbookIds]
    }
  };
}
function checkIsTeammateCoachingSettings(settings) {
  return "coaching_focus" in settings;
}
function getAutomationPlaybookIds(settings) {
  if ("report_config" in settings) {
    return [...settings.report_config.playbook_ids];
  }
  return [settings.playbook_id];
}
function getAutomationPrimaryPlaybookId(settings) {
  return getAutomationPlaybookIds(settings)[0];
}
function getAutomationSettingsSchema(templateId) {
  switch (templateId) {
    case AutomationTemplateIds.TEAMMATE_COACHING:
      return teammateCoachingAutomationSettingsSchema;
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT:
    case AutomationTemplateIds.LEAD_BURNING:
      return managerReportSettingsSchema;
    case AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT:
      return playbookFeedbackAlertSettingsSchema;
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
    case AutomationTemplateIds.STANDOUT_CALLS:
    case AutomationTemplateIds.CUSTOM:
      return customAutomationSettingsSchema;
    default:
      assertUnreachable(templateId);
  }
}
const automationTemplates = [
  {
    id: AutomationTemplateIds.CUSTOM,
    name: "Custom automation",
    description: "Looks for anything you describe in selected calls and shares a summary with supporting call moments.",
    overviewMarkdown: `Custom automations monitor your calls for a pattern, risk, opportunity, or theme you describe in plain language. When a match is found, Alpharun sends the right people a summary with supporting call evidence.

#### What it looks at

- The calls on the playbook you choose, over the schedule you set
- Whatever pattern, risk, opportunity, or theme you describe \u2014 an objection, a missed compliance step, a competitor mention, or a great call worth celebrating

#### What you'll get

- One summary per run with exactly what you asked to receive
- Call evidence and quotes backing each finding
- Delivery by email to the teammates and groups you select
- On frequent schedules, a notification only when something qualifies`
  },
  {
    id: AutomationTemplateIds.TEAMMATE_COACHING,
    name: "Weekly coaching goals",
    description: "Sends weekly coaching goals to teammates, managers, or both by email, Slack or Teams. Managers can add guidance on what to focus on.",
    overviewMarkdown: `Alpharun generates weekly coaching goals for every teammate with enough call activity and shows them on the teammate profile. This automation sends teammates their new goal and a recap of last week's progress on Monday at 8am, a progress check in on Wednesday at noon, and another progress check in on Friday at noon. Managers receive a digest of their team's goals every Monday. Managers can also add coaching focus guidance so Alpharun prioritizes the criteria that matter most.

#### What Alpharun uses

- Each teammate's AI generated weekly coaching goals
- How each teammate is performing against each playbook criteria
- Which behaviors are having the biggest impact on their score
- What top teammates are doing differently
- Each teammate's week over week performance metrics
- Notable moments from recent calls tied to the coaching theme

#### What this automation controls

- Monday, Wednesday, and Friday delivery to teammates by email, Slack DM or Teams DM
- Monday digest delivery to managers through the same channel
- Whether teammates, managers, or both receive the goals
- A manager digest with every teammate's goals in one view
- Optional coaching focus guidance for how goals should be prioritized`
  },
  {
    id: AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST,
    name: "Customer praise digest",
    description: "Finds meaningful moments when customers praise your product, service, or team. It captures what stood out in their own words.",
    overviewMarkdown: `Finds moments where customers go noticeably out of their way to praise your company, product, service, or team. Each result includes the customer's words, what they praised, helpful context, and a link to the call.

#### What it looks at

- Meaningful product or service outcomes
- Favorable comparisons to alternatives or competitors
- Unprompted praise for a specific teammate
- Strong expressions of trust, relief, confidence, or appreciation

#### What you'll get

- A shared digest of every meaningful praise moment found
- The customer's quote and what they were praising
- A short explanation of why the moment matters
- A link to the exact moment in the call`
  },
  {
    id: AutomationTemplateIds.STANDOUT_CALLS,
    name: "Standout calls",
    description: "Celebrates successful, high-scoring calls with sales behaviors the whole team can learn from.",
    overviewMarkdown: `Finds the strongest successful sales consultations and celebrates the specific behaviors that made them worth learning from.

#### What it looks at

- Successful sales consultations with an overall playbook score of at least 90%
- Specific, repeatable sales behaviors executed unusually well
- Strong transcript evidence showing the teammate's execution and customer impact
- Calls without a material failure, compliance concern, or poor customer experience

#### What you'll get

- Up to four standout calls featuring different teammates
- A concise explanation of what made each call exceptional
- A practical behavior other teammates can repeat
- Linked teammate quotes and meaningful customer responses when they strengthen the example`
  },
  {
    id: AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT,
    name: "Playbook feedback alert",
    description: "Notifies team managers when new playbook feedback comes in, so nothing gets missed.",
    overviewMarkdown: `Notifies team managers when new playbook feedback comes in, so nothing gets missed.

#### What it looks at

- New grading feedback submitted for the selected playbooks
- The playbook criteria connected to each piece of feedback
- Guidance that could improve grading accuracy or playbook quality

#### What you'll get

- A timely alert for each new feedback submission
- The affected playbook criteria and the submitted guidance
- A direct path back to the feedback so managers can review it`
  },
  {
    id: AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT,
    name: "Playbook score movement alert",
    description: "Tracks weekly changes in your team's playbook scores, who drove them, and what to do next.",
    overviewMarkdown: `Tracks your team's playbook criteria scores week over week and surfaces what moved, which reps drove it, and what to do about it.

#### What it looks at

- Criteria scores at the team level compared to the prior period
- Which individual reps are driving team-level movements up or down
- The specific behaviors on calls that explain each score change

#### What you'll get

- A summary of overall playbook adherence movement for the period
- One section per criteria that moved, with scores and the behavioral explanation
- A reinforcement or coaching action for each movement so you know exactly what to do next`
  },
  {
    id: AutomationTemplateIds.LEAD_BURNING,
    name: "Lead burning alert",
    description: "Alerts managers when teammates move through live leads without meaningful conversations.",
    overviewMarkdown: `Periodically analyzes your team's calls to identify team members who are cycling through live leads without meaningful engagement. When a pattern is detected, managers receive an alert with call evidence, transcript examples, and a recommended action so they can intervene quickly.

#### What it looks at

- Team members with a high share of ultra-short live calls relative to their peers
- Clusters of consecutive burned leads within a short time window
- Calls where early playbook sections are marked Not Applicable, confirming no real conversation started

#### What you'll get

- A flagged list of team members showing their short-call rate compared to the team average
- Transcript quotes from burned calls \u2014 only from calls where a live customer picked up and a real conversation failed to start, never voicemails or no-answers
- A recommended action for each flagged team member so managers know exactly what to do
- Notifications only when lead burning is detected`
  }
];
function getAutomationTemplateById(templateId) {
  const template = automationTemplates.find(({ id }) => id === templateId);
  if (!template) {
    throw new Error(`Unknown automation template: ${templateId}`);
  }
  return template;
}
function buildDefaultManagerReportConfig(playbookId) {
  return {
    playbook_ids: [playbookId],
    definitions: [
      {
        instances: { type: "manager", selection: { type: "all" } },
        source: "calls",
        routing: {
          type: "instance_recipient",
          subscribers: buildEmptyRecipientSelection()
        }
      }
    ]
  };
}
function buildDefaultAutomationSettings(templateId, playbookId) {
  switch (templateId) {
    case AutomationTemplateIds.TEAMMATE_COACHING:
      return {
        playbook_id: playbookId,
        coaching_focus: "",
        recipients: {
          includes_all_teammates: true,
          includes_all_managers: true,
          teammate_ids: [],
          group_ids: []
        },
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT:
      return {
        instructions: "",
        trigger: {
          frequency: "weekly",
          weekdays: ["monday"],
          time_of_day: "09:00"
        },
        report_config: buildDefaultManagerReportConfig(playbookId),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT:
      return {
        instructions: "",
        trigger: {
          frequency: "weekly",
          weekdays: ["friday"],
          time_of_day: "17:00"
        },
        report_config: buildAutomationReportConfig(
          "shared",
          [playbookId],
          buildEmptyRecipientSelection()
        ),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.LEAD_BURNING:
      return {
        instructions: "",
        trigger: {
          frequency: "daily",
          time_of_day: LEAD_BURNING_DAILY_TIME_OF_DAY
        },
        report_config: buildDefaultManagerReportConfig(playbookId),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
      return {
        instructions: "",
        trigger: {
          frequency: "weekly",
          weekdays: ["friday"],
          time_of_day: "17:00"
        },
        report_config: buildAutomationReportConfig(
          "shared",
          [playbookId],
          buildEmptyRecipientSelection()
        ),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.STANDOUT_CALLS:
      return {
        instructions: "",
        trigger: {
          frequency: "weekly",
          weekdays: ["friday"],
          time_of_day: "09:00"
        },
        report_config: buildAutomationReportConfig(
          "shared",
          [playbookId],
          buildEmptyRecipientSelection()
        ),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.CUSTOM:
      return {
        instructions: "",
        trigger: {
          frequency: "daily",
          time_of_day: "09:00"
        },
        report_config: buildAutomationReportConfig(
          "shared",
          [playbookId],
          buildEmptyRecipientSelection()
        ),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    default:
      assertUnreachable(templateId);
  }
}
export {
  AutomationDeliveryProvider,
  AutomationTemplateIds,
  DEFAULT_AUTOMATION_DELIVERY,
  LEAD_BURNING_DAILY_TIME_OF_DAY,
  allAutomationManagerScopes,
  allAutomationResultModes,
  allAutomationTemplateIds,
  asAutomationId,
  automationChatDeliveryModeSchema,
  automationDeliveryProviderSchema,
  automationDeliverySchema,
  automationGroupSelectionSchema,
  automationIdSchema,
  automationManagerRecipientsSchema,
  automationManagerScopeSchema,
  automationPeopleSelectionSchema,
  automationPlaybooksScopeSchema,
  automationReportConfigSchema,
  automationReportDefinitionSchema,
  automationReportInstancesSchema,
  automationReportSettingsSchema,
  automationReportSourceSchema,
  automationResultModeSchema,
  automationSubscriberRecipientsSchema,
  automationTeammateSelectionSchema,
  automationTemplateIdSchema,
  automationTemplates,
  buildDefaultAutomationDeliveryForProvider,
  buildDefaultAutomationSettings,
  buildReportDefinitionKey,
  checkCoachingHasRecipients,
  checkIsOrchestratedAutomationTemplate,
  checkIsSharedReportConfig,
  checkIsTeammateCoachingSettings,
  customAutomationSettingsSchema,
  describeAutomationPeopleSelection,
  encodeAutomationResourceId,
  expandAutomationPeopleSelection,
  getAutomationDeliveryEditorValue,
  getAutomationManagerRecipientsEditorValue,
  getAutomationPeopleSelection,
  getAutomationPlaybookIds,
  getAutomationPrimaryPlaybookId,
  getAutomationRecipientSelection,
  getAutomationResultMode,
  getAutomationSettingsSchema,
  getAutomationSubscribersEditorValue,
  getAutomationTemplateById,
  getCoachingRecipients,
  managerReportSettingsSchema,
  partitionAutomationPeopleSelection,
  playbookFeedbackAlertSettingsSchema,
  readCoachingDeliveryProvider,
  teammateCoachingAutomationSettingsSchema,
  teammateCoachingDeliveryProviderSchema,
  teammateCoachingTransportSchema,
  updateAutomationDeliveryEditorValue,
  updateAutomationManagerRecipientsEditorValue,
  updateAutomationPeopleSelection,
  updateAutomationPlaybookIds,
  updateAutomationRecipientSelection,
  updateAutomationResultMode,
  updateAutomationSubscribersEditorValue,
  updateCoachingDeliveryProvider,
  updateCoachingRecipients
};
//# sourceMappingURL=automation.js.map
